import { userService } from "@/services/UserService";
import { toast } from "vue3-toastify";
import { i18n } from "./../lang";

const PATCH_ONLY_USER_FIELDS = [
    'pairingCode',
    'appMode',
    'kdsSubscribedProductGroupIds',
    'userSubscribedProductGroupIds',
    'userSubscribedZoneIds'
];

function withoutPairingCode(user) {
    const {pairingCode, ...savedUser} = user;
    return savedUser;
}

const state = {
    users: [],
    deletedUserId: null,
    userRequestMessage: "",
    isLoadingUserRequest: false,
    editingUser: null,
    editRequest: {
        loading: false,
    },
    isAddingUser: false,
    addRequest: {
        loading: false,
    },
    duplicatingUser: null,
    duplicateRequest: {
        loading: false,
    },
    isLinkingDevice: false,
    linkingDeviceUser: null,
    linkDeviceRequest: {
        loading: false,
    }
};

const actions = {
    async getAllUsers({dispatch, commit, rootState}){
        const user = rootState.account?.user || await new Promise(resolve => {
            const stop = this.watch(state => state.account.user, value => {
                if (!value) return;
                stop();
                resolve(value);
            });
        });
        if (user.type !== "ADMIN" && user.type !== "SUPER_USER") return;
        commit(mutations.setLoadingRequest.name, true);
        userService.getAllUsers()
            .then((response) => {
                commit('setLoadingRequest', false);
                commit(mutations.setUsersList.name, response.data);
            })
            .catch((error) => {
                commit(mutations.setLoadingRequest.name, false, error);
            });
    },
    deleteUser({dispatch, commit, state}, user){
        commit(mutations.setUserEditRequestStarted.name);
        userService.deleteUser(user.userId)
            .then((response) => {
                toast.success(i18n.global.t("toast.deleteSuccess"));
                const updatedUsers = state.users.filter(function(element) {
                    return element.userId !== user.userId;
                  });
                commit(mutations.setEditRequestSuccess.name);
                commit(mutations.updateUsersList.name, updatedUsers);
            })
            .catch((error) => {
                toast.error(i18n.global.t("toast.deleteError"));
                commit(mutations.setEditRequestError.name, error)
            })
    },
    createUser({commit, dispatch}, user){
        commit(mutations.setAddUserRequestState.name, {loading: true});
        const patchOnlyFields = {};
        PATCH_ONLY_USER_FIELDS.forEach((field) => {
            const value = user[field];
            if (value == null || value === '') return;
            if (Array.isArray(value) && value.length === 0) return;
            if (field === 'appMode' && value === 'APP') return;
            patchOnlyFields[field] = value;
        });

        const {pairingCode, ...createdFields} = user;
        return userService.createUser(createdFields)
            .then((response) => {
                const createdUser = response.data;
                if (!Object.keys(patchOnlyFields).length) {
                    toast.success(i18n.global.t("toast.createSuccess"));
                    commit(mutations.setAddUserRequestState.name, {loading: false});
                    commit(mutations.addNewUser.name, createdUser);
                    return;
                }
                const fullUser = {...createdUser, ...patchOnlyFields};
                return userService.updateUser(fullUser)
                    .then(() => {
                        toast.success(i18n.global.t("toast.createSuccess"));
                        commit(mutations.setAddUserRequestState.name, {loading: false});
                        commit(mutations.addNewUser.name, withoutPairingCode(fullUser));
                    })
                    .catch((error) => {
                        toast.error(i18n.global.t(pairingCode ? "userPage.createLinkDeviceError" : "userPage.createPartialError", {error}));
                        commit(mutations.setAddUserRequestState.name, {loading: false});
                        commit(mutations.addNewUser.name, createdUser);
                        if (pairingCode) dispatch('getAllUsers');
                    });
            })
            .catch((error) => {
                toast.error(i18n.global.t("toast.createError"));
                commit(mutations.setAddUserRequestState.name, {loading: false, message: error})
            })
    },
    duplicateUser({commit}, {sourceUser, username, password, pincode, email = null}){
        commit(mutations.setDuplicateUserRequestState.name, {loading: true});
        return userService.createUser({
            username,
            password,
            pincode,
            type: sourceUser.type,
            terminalIp: sourceUser.terminalIp,
            niss: sourceUser.niss,
            blackboxTester: sourceUser.blackboxTester,
            abilities: sourceUser.abilities,
            printer: sourceUser.printer,
            vwTerminalId: sourceUser.vwTerminalId,
            vwMultiMerchantId: sourceUser.vwMultiMerchantId,
            kdsCallMode: sourceUser.kdsCallMode,
            email,
            phoneNumber: sourceUser.phoneNumber
        })
            .then((response) => {
                const createdUser = response.data;
                const duplicatedUser = {
                    ...sourceUser,
                    userId: createdUser.userId,
                    creationTimestamp: createdUser.creationTimestamp,
                    username,
                    password,
                    pincode,
                    email
                };
                return userService.updateUser(duplicatedUser)
                    .then(() => {
                        toast.success(i18n.global.t("toast.createSuccess"));
                        commit(mutations.setDuplicateUserRequestState.name, {loading: false});
                        commit(mutations.addNewUser.name, duplicatedUser);
                    });
            })
            .catch((error) => {
                toast.error(i18n.global.t("toast.createError"));
                commit(mutations.setDuplicateUserRequestState.name, {loading: false, message: error});
            })
    },
    linkDevice({commit}, {userId, code}){
        commit(mutations.setLinkDeviceRequestState.name, {loading: true});
        return userService.linkDevice(userId, code)
            .then(() => {
                toast.success(i18n.global.t("userPage.linkDeviceSuccess"));
                commit(mutations.setLinkDeviceRequestState.name, {loading: false});
            })
            .catch((error) => {
                toast.error(error || i18n.global.t("userPage.linkDeviceError"));
                commit(mutations.setLinkDeviceRequestState.name, {loading: false, message: error});
            });
    },
    updateUser({dispatch, commit, state}, user) {
        commit(mutations.setUserEditRequestStarted.name);
        userService.updateUser(user)
            .then(() => {
                toast.success(i18n.global.t("toast.successEdit"));
                const updatedUsers = state.users.map(x => (x.userId === user.userId ? withoutPairingCode(user) : x));
                commit(mutations.setEditRequestSuccess.name);
                commit(mutations.updateUsersList.name, updatedUsers);
            })
            .catch((error) => {
                toast.error(i18n.global.t("toast.errorEdit"));
                commit(mutations.setEditRequestError.name, error)
            });
    }
}

const mutations = {
    setLoadingRequest(state, isLoading, message=''){
        state.userRequestMessage = message;
        state.isLoadingUserRequest = isLoading;
    },
    setUsersList(state, users){
        state.users = users;
    },
    setDeletedUserId(state, userId){
        
        state.deletedUserId = userId;
    },
    setUserEdit(state, editingUser){
        state.editingUser = editingUser;
        //in order to clear previous errors
        state.editRequest = {
            loading: false
        }
    },
    setUserEditRequestStarted(state){
        state.editRequest = {
            loading: true
        }
    },
    setEditRequestSuccess(state){
        state.editRequest = {
            loading: false,
            success: true
        }
        state.editingUser = null;
    },
    setEditRequestError(state, errorMessage){
        state.editRequest = {
            loading: false,
            success: false,
            message: errorMessage
        }
    },
    setAddingUser(state, isAdding) {
        state.isAddingUser = isAdding;
        if (!isAdding){
            state.addRequest = {};
        }
    },
    setAddUserRequestState(state, {loading, message = null}){
        state.addRequest = {
            loading: loading,
            message: message,
        }

        if (!loading && !message){
            state.isAddingUser = false;
        }
    },
    setDuplicatingUser(state, user){
        state.duplicatingUser = user;
        state.duplicateRequest = {
            loading: false
        }
    },
    setDuplicateUserRequestState(state, {loading, message = null}){
        state.duplicateRequest = {
            loading: loading,
            message: message,
        }
        if (!loading && !message){
            state.duplicatingUser = null;
        }
    },
    openLinkDevice(state, user = null){
        state.isLinkingDevice = true;
        state.linkingDeviceUser = user;
        state.linkDeviceRequest = {
            loading: false
        }
    },
    setLinkDeviceRequestState(state, {loading, message = null}){
        state.linkDeviceRequest = {
            loading: loading,
            message: message,
        }
        if (!loading && !message){
            state.isLinkingDevice = false;
            state.linkingDeviceUser = null;
        }
    },
    closeLinkDevice(state){
        state.isLinkingDevice = false;
        state.linkingDeviceUser = null;
    },
    updateUsersList(state, newUsersList){
        state.users = newUsersList;
    },
    addNewUser(state, newUser){
        state.users.push(newUser);
    }
}


export const user = {
    namespaced: true,
    state,
    actions,
    mutations
};